import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import posthog from 'posthog-js';

import { client } from 'client/client.gen';

const baseURL = import.meta.env.VITE_APP_BASE_URL || '';
const [, domain = ''] = baseURL.split('://');
const [host] = domain.split(':');

const dsn = import.meta.env.VITE_APP_SENTRY_DSN;
const environment = import.meta.env.VITE_APP_SENTRY_ENV;
const sentryOrg = import.meta.env.VITE_APP_SENTRY_ORG;
const sentryProjectId = import.meta.env.VITE_APP_SENTRY_PROJECT_ID;
const posthogApiHost = import.meta.env.VITE_APP_POSTHOG_API_HOST;
const posthogToken = import.meta.env.VITE_APP_POSTHOG_TOKEN;

const isDev = environment === 'development';

// use fake token for local development to enable bootstrap options
posthog.init(posthogToken || (isDev ? environment : ''), {
  api_host: posthogApiHost,
  // Let the SDK own pageview/pageleave on SPA route changes so each $pageview
  // gets a paired $pageleave (required for engaged-time / session-duration /
  // bounce metrics). We previously captured $pageview manually on route change,
  // which left client-side navigations without a matching $pageleave. The manual
  // capture in routes/__root.tsx has been removed in favour of this.
  capture_pageview: 'history_change',
  bootstrap: {
    featureFlags: {
      'project-submissions': isDev,
      'project-account-plan': isDev,
      'project-account-plan-2': isDev,
      'project-acrew': isDev,
      'project-admin-hub': isDev,
      // 'project-experiments': isDev,
      // 'project-ai-candidate-discovery': isDev,
      'project-pipeline-discovery': isDev,
      'project-tasks': isDev,
      'project-user-management': isDev,
      'project-exec-tech': isDev,
      'developers-only': isDev,
      // keep sample project to demonstrate naming pattern
      // project-sample: true,
    },
  },
});

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        blockAllMedia: false,
        maskAllInputs: false,
        maskAllText: false,
        networkDetailAllowUrls: [host || window.location.origin],
        networkRequestHeaders: ['authorization'],
      }),
      posthog.sentryIntegration({
        organization: sentryOrg,
        projectId: Number.parseInt(sentryProjectId!, 10),
      }),
    ],
    replaysOnErrorSampleRate: process.env.NODE_ENV === 'production' ? 1.0 : 0,
    replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 1.0 : 0,
    tracesSampleRate: 0.3,
  });

  client.instance.interceptors.response.use(undefined, (error: AxiosError) => {
    if (error.response?.status === 405) {
      Sentry.captureException(error);
    }
    return Promise.reject(error);
  });
}
